import { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import Chart, { SCALE_TYPES } from "./Chart";
import CustomToggle, { CustomToggleOption } from "../CustomToggle/CustomToggle";

enum TIME_FILTER {
  WEEK = "week",
  MONTH = "month",
  YEAR = "year",
  ALL = "all",
}

interface ChartByTimeProps {
  xData: Array<Date>;
  yData: Array<number>;
  withMonth: boolean;
  title?: string;
  valueFormatter?: Function;
  showButtons?: boolean;
  bars?: boolean;
}

const ChartByTime = ({
  title,
  xData,
  yData,
  withMonth,
  valueFormatter,
  showButtons,
  bars,
}: ChartByTimeProps) => {
  const [filter, setFilter] = useState<TIME_FILTER | undefined>(
    TIME_FILTER.ALL
  );
  const [filteredXData, setFilteredXData] = useState<Array<Date>>(xData);
  const [filteredYData, setFilteredYData] = useState<Array<number>>(yData);

  const matches = useMediaQuery("(min-width:900px)");

  const filterDataByTime = (
    xData: Date[],
    yData: number[],
    filter: TIME_FILTER
  ): { xFiltered: Date[]; yFiltered: number[] } => {
    const currentDate = new Date();

    switch (filter) {
      case TIME_FILTER.WEEK:
        const oneWeekAgo = new Date(
          currentDate.getTime() - 7 * 24 * 60 * 60 * 1000
        );
        const filteredIndexWeek = xData
          .map((date, index) => (date >= oneWeekAgo ? index : -1))
          .filter((index) => index !== -1);
        return {
          xFiltered: filteredIndexWeek.map((index) => xData[index]),
          yFiltered: filteredIndexWeek.map((index) => yData[index]),
        };

      case TIME_FILTER.MONTH:
        const oneMonthAgo = new Date(
          currentDate.getTime() - 30 * 24 * 60 * 60 * 1000
        );
        const filteredIndexMonth = xData
          .map((date, index) => (date >= oneMonthAgo ? index : -1))
          .filter((index) => index !== -1);
        return {
          xFiltered: filteredIndexMonth.map((index) => xData[index]),
          yFiltered: filteredIndexMonth.map((index) => yData[index]),
        };

      case TIME_FILTER.YEAR:
        const oneYearAgo = new Date(
          currentDate.getTime() - 365 * 24 * 60 * 60 * 1000
        );
        const filteredIndexYear = xData
          .map((date, index) => (date >= oneYearAgo ? index : -1))
          .filter((index) => index !== -1);
        return {
          xFiltered: filteredIndexYear.map((index) => xData[index]),
          yFiltered: filteredIndexYear.map((index) => yData[index]),
        };

      case TIME_FILTER.ALL:
        return {
          xFiltered: xData,
          yFiltered: yData,
        };

      default:
        throw new Error("Time frame filter not valid");
    }
  };

  const dateFormatter = (value: Date) => {
    let result = value.getFullYear().toString();

    if (withMonth) {
      const month = value.getMonth() + 1;
      result += "/" + month.toString();
    }

    return result;
  };

  useEffect(() => {
    if (showButtons === true && filter !== undefined) {
      const { xFiltered, yFiltered } = filterDataByTime(xData, yData, filter);
      setFilteredXData(xFiltered);
      setFilteredYData(yFiltered);
    }
  }, [filter]);

  useEffect(() => {
    setFilteredXData(xData);
  }, [xData]);

  useEffect(() => {
    setFilteredYData(yData);
  }, [yData]);

  const toggleOptions: Array<CustomToggleOption> = [
    { label: "7D", value: TIME_FILTER.WEEK },
    { label: "30D", value: TIME_FILTER.MONTH },
    { label: "1Y", value: TIME_FILTER.YEAR },
    { label: "All", value: TIME_FILTER.ALL },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {title && (
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "700px !important" }}>
            {title}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column-reverse", sm: "row" },
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", width: showButtons ? "87.5%" : "100%" }}>
          <Chart
            xData={filteredXData}
            yData={filteredYData}
            scaleType={bars ? SCALE_TYPES.BAND : SCALE_TYPES.TIME}
            xDataFormatter={dateFormatter}
            yDataFormatter={valueFormatter}
            bars={bars}
          />
        </Box>
        {showButtons && (
          <Box sx={{ display: "flex", paddingTop: "29px", width: "12.5%" }}>
            <CustomToggle
              options={toggleOptions}
              setValue={setFilter}
              value={filter}
              direction={matches ? "column" : "row"}
              fullWidth={true}
              itemSize={matches ? 75 : "auto"}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChartByTime;
